"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBranchEnergyCalculation = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const types_1 = require("../../types");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const pollingConstants_1 = require("../../utils/pollingConstants");
const stableValue_1 = require("../../utils/stableValue");
const useStableList_1 = require("../useStableList");
const NUMBER_OF_DATAPOINTS_FOR_REALTIME_CALCULATION = 6;
// Anything at/above this is treated as split-phase: the CT clamps one leg, so the actual load
// voltage is ~2x the single-leg reading. Matches the threshold used in useElectricalWithHVAC.
const SPLIT_PHASE_VOLTAGE_THRESHOLD = 180;
const useBranchEnergyCalculation = ({ selectedHub, sensors }) => {
    const [branchEnergyCalculationData, setBranchEnergyCalculationData] = (0, react_1.useState)([]);
    const [branchPowerMonitorData, setBranchPowerMonitorData] = (0, react_1.useState)([]);
    const [groupedBranchData, setGroupedBranchData] = (0, react_1.useState)([]);
    const [isValidConfiguration, setIsValidConfiguration] = (0, react_1.useState)(false);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [allSensorEvents, setAllSensorEvents] = (0, react_1.useState)(new Map());
    const [mainCtTotalWatts, setMainCtTotalWatts] = (0, react_1.useState)(0);
    const powerMonitorSensors = (0, useStableList_1.useStableList)(sensors.filter((sensor) => sensor.hubId === selectedHub?.id && sensor.type === types_1.SensorType.POWER));
    // Loading is for the first fetch only: flipping it on every 30 s poll re-rendered every
    // consumer twice per poll for nothing.
    const loadedOnce = (0, react_1.useRef)(false);
    // Keep backward compat: expose first sensor
    const powerMonitorSensor = powerMonitorSensors[0] ?? undefined;
    const prevSensorIdsRef = (0, react_1.useRef)('');
    const fetchReadings = (0, react_1.useCallback)(async () => {
        if (powerMonitorSensors.length === 0) {
            setAllSensorEvents(new Map());
            return;
        }
        if (!loadedOnce.current)
            setLoading(true);
        setError(null);
        try {
            // Per-sensor catch: one flaky sensor's rejection must not discard the
            // whole batch and freeze every other monitor's branch data.
            const results = await Promise.all(powerMonitorSensors.map(async (sensor) => {
                try {
                    const response = await amplifyBootstrap_1.API.graphql({
                        query: queries_1.getSensorEventsBySensor,
                        variables: {
                            sensorId: sensor.id,
                            limit: NUMBER_OF_DATAPOINTS_FOR_REALTIME_CALCULATION,
                        },
                    });
                    const events = response.data?.getSensorEventsBySensor?.items ||
                        response.data?.getSensorEventsBySensor ||
                        [];
                    return { sensorId: sensor.id, events: events.map(parseSensorEvent) };
                }
                catch (err) {
                    console.error(`Error fetching branch events for sensor ${sensor.id}:`, err);
                    return null;
                }
            }));
            const successes = results.filter((r) => r !== null);
            if (successes.length === 0 && powerMonitorSensors.length > 0) {
                setError(new Error('Failed to fetch branch events for all power monitors'));
                return;
            }
            const eventsMap = new Map();
            successes.forEach(({ sensorId, events }) => {
                eventsMap.set(sensorId, events);
            });
            // Same readings as last poll → same reference → nothing downstream recomputes.
            setAllSensorEvents((prev) => (0, stableValue_1.stable)(prev, eventsMap));
        }
        catch (err) {
            setError(err);
        }
        finally {
            loadedOnce.current = true;
            setLoading(false);
        }
    }, [powerMonitorSensors.map((s) => s.id).join(',')]);
    // Fetch when sensors change
    (0, react_1.useEffect)(() => {
        const sensorIds = powerMonitorSensors.map((s) => s.id).join(',');
        if (sensorIds !== prevSensorIdsRef.current) {
            prevSensorIdsRef.current = sensorIds;
            fetchReadings();
        }
    }, [powerMonitorSensors, fetchReadings]);
    // Poll on the same cadence as HVAC so branch CT readings refresh while the user
    // sits on the Electrical screen. The sensor-change effect handles the initial
    // fetch, so the interval just keeps the data flowing afterwards. Without this,
    // consumers like ElectricalDetailsScreen go stale until the user leaves and
    // re-enters the tab.
    (0, react_1.useEffect)(() => {
        if (powerMonitorSensors.length === 0)
            return;
        const interval = setInterval(fetchReadings, pollingConstants_1.POLLING_INTERVALS_MILLIS.ELECTRICAL);
        return () => clearInterval(interval);
    }, [fetchReadings, powerMonitorSensors.length]);
    // Process readings when events or sensors change
    (0, react_1.useEffect)(() => {
        const hasConfigured = powerMonitorSensors.some((s) => s.branchCircuits && s.branchCircuits.length > 0);
        if (!hasConfigured) {
            setIsValidConfiguration(false);
            setBranchEnergyCalculationData([]);
            setBranchPowerMonitorData([]);
            setGroupedBranchData([]);
            setMainCtTotalWatts(0);
            return;
        }
        setIsValidConfiguration(true);
        if (allSensorEvents.size === 0) {
            return;
        }
        // Collect branch power data per sensor and combined
        const allBranchEnergy = [];
        const allBranchMonitor = [];
        const grouped = [];
        let mainCtWatts = 0;
        for (const sensor of powerMonitorSensors) {
            const events = allSensorEvents.get(sensor.id);
            if (!events || events.length === 0 || !sensor.branchCircuits?.length)
                continue;
            const sensorBranchEnergy = processBranchEnergyCalculation(events, sensor.branchCircuits);
            const sensorBranchMonitor = processBranchPowerMonitorReadings(events, sensor.branchCircuits);
            mainCtWatts += calculateMainCtWattsFromEvents(events);
            allBranchEnergy.push(...sensorBranchEnergy);
            allBranchMonitor.push(...sensorBranchMonitor);
            grouped.push({
                sensorName: sensor.name || sensor.location || `Power Monitor`,
                sensorId: sensor.id,
                branches: sensorBranchMonitor,
            });
        }
        setMainCtTotalWatts(mainCtWatts);
        // Recalculate percentages against combined total power
        const combinedTotalPower = allBranchEnergy.reduce((sum, b) => sum + b.power, 0);
        if (combinedTotalPower > 0) {
            allBranchEnergy.forEach((b) => {
                b.percent = calculatePowerPercentage(b.power, combinedTotalPower);
            });
            allBranchMonitor.forEach((b) => {
                const power = b.branchCT * b.voltage;
                b.percent = calculatePowerPercentage(power, combinedTotalPower);
            });
            // Also update grouped data percentages
            grouped.forEach((group) => {
                group.branches.forEach((b) => {
                    const power = b.branchCT * b.voltage;
                    b.percent = calculatePowerPercentage(power, combinedTotalPower);
                });
            });
        }
        // A power monitor's own report (new lastReported) re-runs this with unchanged readings:
        // keep the previous arrays when the numbers are the same.
        setBranchEnergyCalculationData((prev) => (0, stableValue_1.stable)(prev, allBranchEnergy));
        setBranchPowerMonitorData((prev) => (0, stableValue_1.stable)(prev, allBranchMonitor));
        setGroupedBranchData((prev) => (0, stableValue_1.stable)(prev, grouped));
    }, [powerMonitorSensors, allSensorEvents]);
    return {
        powerMonitorSensor,
        powerMonitorSensors,
        branchEnergyCalculationData,
        branchPowerMonitorData,
        groupedBranchData,
        /**
         * Whole-house instantaneous watts from the panel mains (`main_ct1*vt1 + main_ct2*vt2`),
         * summed across all power-monitor sensors. Zero if there are no readings yet.
         */
        mainCtTotalWatts,
        isValidConfiguration,
        refresh: fetchReadings,
        loading,
        error,
    };
};
exports.useBranchEnergyCalculation = useBranchEnergyCalculation;
function parseSensorEvent(sensorEvent) {
    const parsed = { ...sensorEvent };
    if (sensorEvent.metadata && typeof sensorEvent.metadata === 'string') {
        try {
            parsed.parsedMetadata = JSON.parse(sensorEvent.metadata);
        }
        catch {
            // ignore parse errors
        }
    }
    if (sensorEvent.values && typeof sensorEvent.values === 'string') {
        try {
            parsed.values = JSON.parse(sensorEvent.values);
        }
        catch {
            // keep original
        }
    }
    return parsed;
}
function processBranchEnergyCalculation(events, branchCircuits) {
    events.sort((a, b) => a.timestamp - b.timestamp);
    const packetBuffer = buildPacketData(events);
    const readings = buildPowerMonitorReading(packetBuffer);
    const calculations = calculateBranchEnergyPercentFromReadings(readings, branchCircuits);
    return calculations;
}
function calculateMainCtWattsFromEvents(events) {
    const sorted = [...events].sort((a, b) => a.timestamp - b.timestamp);
    const packetBuffer = buildPacketData(sorted);
    const readings = buildPowerMonitorReading(packetBuffer);
    if (readings.length === 0)
        return 0;
    const latest = readings[readings.length - 1];
    const ct1 = latest.main_ct1 ?? 0;
    const ct2 = latest.main_ct2 ?? 0;
    const vt1 = latest.vt1 ?? 0;
    const vt2 = latest.vt2 ?? 0;
    return ct1 * vt1 + ct2 * vt2;
}
function processBranchPowerMonitorReadings(events, branchCircuits) {
    const sortedEvents = [...events].sort((a, b) => a.timestamp - b.timestamp);
    const packetBuffer = buildPacketData(sortedEvents);
    const readings = buildPowerMonitorReading(packetBuffer);
    if (readings.length === 0) {
        return [];
    }
    const latestReading = readings[readings.length - 1];
    const branchPowers = calculateAccumulatedPowerFromReadings([latestReading], branchCircuits);
    const totalPower = calculateTotalPower(branchPowers);
    const branchData = extractBranchDataFromReading(latestReading, branchCircuits);
    return branchData
        .map(({ ct, voltage, inputNumber }) => {
        const circuit = branchCircuits.find((c) => c.powerMonitorInputNumber === inputNumber);
        if (!circuit || (!circuit.sublocation && !circuit.location)) {
            return null;
        }
        const power = branchPowers[inputNumber - 1];
        const percent = calculatePowerPercentage(power, totalPower);
        return {
            branchIndex: inputNumber,
            branchCT: ct,
            voltage,
            percent,
            name: circuit.sublocation || circuit.location,
            location: circuit.location,
            sublocation: circuit.sublocation,
            timestamp: latestReading.timestamp,
        };
    })
        // Filter out branches that are not configured
        .filter(Boolean);
}
function buildPacketData(events) {
    const packetBuffer = [];
    for (const event of events) {
        const part = event.values?.packet_type;
        const values = event.values;
        const sequence = event.parsedMetadata?.sequenceNumber;
        if (!part || !values || typeof sequence !== 'number')
            continue;
        const newValues = { ...values };
        delete newValues.packet_type;
        packetBuffer.push({
            part,
            values: newValues,
            sequence,
            timestamp: event.timestamp,
        });
    }
    return packetBuffer;
}
function buildPowerMonitorReading(packetBuffer) {
    const readings = [];
    for (let i = 0; i < packetBuffer.length; i++) {
        const a = packetBuffer[i];
        if (a.part !== 'A')
            continue;
        const b = packetBuffer.find((p) => p.sequence === a.sequence + 1 && p.part === 'B');
        const c = packetBuffer.find((p) => p.sequence === a.sequence + 2 && p.part === 'C');
        if (b && c) {
            readings.push({
                ...a.values,
                ...b.values,
                ...c.values,
                timestamp: a.timestamp,
            });
        }
    }
    return readings;
}
function calculatePowerPercentage(power, totalPower) {
    return totalPower > 0 ? parseFloat(((power / totalPower) * 100).toFixed(2)) : 0;
}
function calculateTotalPower(powerValues) {
    return powerValues.reduce((sum, val) => sum + val, 0);
}
function extractBranchDataFromReading(reading, branchCircuits) {
    const raw = [
        { ct: reading.branch_ct1, voltage: reading.vt1, inputNumber: 1 },
        { ct: reading.branch_ct2, voltage: reading.vt1, inputNumber: 2 },
        { ct: reading.branch_ct3, voltage: reading.vt1, inputNumber: 3 },
        { ct: reading.branch_ct4, voltage: reading.vt1, inputNumber: 4 },
        { ct: reading.branch_ct5, voltage: reading.vt1, inputNumber: 5 },
        { ct: reading.branch_ct6, voltage: reading.vt1, inputNumber: 6 },
        { ct: reading.branch_ct7, voltage: reading.vt1, inputNumber: 7 },
        { ct: reading.branch_ct8, voltage: reading.vt2, inputNumber: 8 },
        { ct: reading.branch_ct9, voltage: reading.vt2, inputNumber: 9 },
        { ct: reading.branch_ct10, voltage: reading.vt2, inputNumber: 10 },
        { ct: reading.branch_ct11, voltage: reading.vt2, inputNumber: 11 },
        { ct: reading.branch_ct12, voltage: reading.vt2, inputNumber: 12 },
        { ct: reading.branch_ct13, voltage: reading.vt2, inputNumber: 13 },
        { ct: reading.branch_ct14, voltage: reading.vt2, inputNumber: 14 },
        { ct: reading.branch_ct15, voltage: reading.vt2, inputNumber: 15 },
        { ct: reading.branch_ct16, voltage: reading.vt2, inputNumber: 16 },
    ];
    if (!branchCircuits?.length)
        return raw;
    // For 220V split-phase circuits, the CT measures one leg's current but the actual load voltage
    // is double the leg reading. Doubling here (rather than at every consumer) means downstream
    // watts/percent math (`branchCT * voltage`) is automatically correct for split-phase branches.
    return raw.map((entry) => {
        const circuit = branchCircuits.find((c) => c.powerMonitorInputNumber === entry.inputNumber);
        if (circuit?.voltage && circuit.voltage >= SPLIT_PHASE_VOLTAGE_THRESHOLD) {
            return { ...entry, voltage: entry.voltage * 2 };
        }
        return entry;
    });
}
function calculateAccumulatedPowerFromReadings(readings, branchCircuits) {
    const totals = new Array(16).fill(0);
    for (const reading of readings) {
        const branchData = extractBranchDataFromReading(reading, branchCircuits);
        branchData.forEach((branch, idx) => {
            totals[idx] += branch.ct * branch.voltage;
        });
    }
    return totals;
}
function calculateBranchEnergyPercentFromReadings(readings, branchCircuits) {
    if (readings.length === 0) {
        return [];
    }
    const latestReading = readings[readings.length - 1];
    const branchPowers = calculateAccumulatedPowerFromReadings([latestReading], branchCircuits);
    const totalPower = calculateTotalPower(branchPowers);
    return branchPowers
        .map((power, index) => {
        const inputNumber = index + 1;
        const circuit = branchCircuits.find((c) => c.powerMonitorInputNumber === inputNumber);
        if (!circuit || (!circuit.sublocation && !circuit.location)) {
            return null;
        }
        return {
            branchIndex: inputNumber,
            power: parseFloat(power.toFixed(2)),
            percent: calculatePowerPercentage(power, totalPower),
            name: circuit.sublocation || circuit.location,
            location: circuit.location,
            sublocation: circuit.sublocation,
        };
    })
        .filter((entry) => entry !== null);
}
